import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Accordion } from "react-bootstrap";
import "../../assets/css/sidebar.css"; // Import the new CSS file
import MainSession from "./MainSession"; // Import the new MainSession component
import Checklist from "./Checklist";
import { isCurrentSessionSideType } from "../../chatSession"; // Import the isCurrentSessionSideType function
import NotificationSession from "./NotificationSession";
import CustomChatBot from "./CustomChatBot";

import { supabase } from "../../supabaseClient";
import NotesList from "./NotesList";
import List from "./List";
import GroceryList from "./GroceryList";
import IdeasList from "./IdeasList";
import SharedWithMe from "./SharedWithMe";
import ShareSectionPopup from "../common/ShareSectionPopup";
import Cookies from "js-cookie";
import OpenAIRealtimeTest from "../OpenAIRealtimeTest";
import SidebarCredits from "../billing/SidebarCredits";


function Sidebar({
  toggleTheme,
  isDarkMode,
  userId,
  isCategoryUpdate,
  isNotesCategoryUpdate,
  isListCategoryUpdate,
  isTaskChannelUpdate,
  isListDataChannelUpdate,
  isNotesDataChannelUpdate,
  isSideConvoChannelUpdate,
  isNotificationSessionChannel,
  isActiveSideConvoChannel,
  isActiveCustomBotChannel,
  isTaskProfileChannel,
  isListProfileChannel,
  isNotesProfileChannel,
  isSidebarVisible,
  onTabChange,
  // Voice chat callbacks
  onVoiceStart,
  onVoiceEnd,
  onVoiceTranscriptUpdate,
  onVoiceVideoChange, // (videoEnabled, isScreen) => void - notify when video/screen state changes
  // Voice control ref - passed from App.jsx for external control from ChatBot
  voiceRef,
  // Voice active state - shows Voice Assistant menu when active
  isVoiceActive,
  onOpenReminder,
  onOpenScheduledTasks,
}) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [defaultActiveKey, setDefaultActiveKey] = useState(null); // Initialize as null
  const [loading, setLoading] = useState(true); // Add loading state
  const [userProfile, setUserProfile] = useState(null);
  const [customChatBotList, setCustomChatBotList] = useState(null); // Add loading state

  const [sharingPendingCount, setSharingPendingCount] = useState(0);
  const [sharingBreakdown, setSharingBreakdown] = useState({ pending: 0, tasks: 0, lists: 0, notes: 0, grocery: 0 });
  const [showShareTaskPopup, setShowShareTaskPopup] = useState(false);
  const [showShareListPopup, setShowShareListPopup] = useState(false);
  const [showShareNotesPopup, setShowShareNotesPopup] = useState(false);
  const [showShareGroceryPopup, setShowShareGroceryPopup] = useState(false);

  // ADD THESE NEW REFS
  const checklistRef = useRef(null);
  const listRef = useRef(null);
  const notesListRef = useRef(null);
  const groceryListRef = useRef(null);
  const ideasListRef = useRef(null);

  // ADD THIS NEW FUNCTION
  const handleAccordionHeaderClick = (eventKey, event) => {
    // Don't interfere with Bootstrap accordion's normal operation
    // Only call our function if it's NOT a click on the actual toggle button
    
    // Check if clicked element or any parent is the actual button that toggles accordion
    const isAccordionButton = event.target.closest('.accordion-button') !== null;
    
    if (!isAccordionButton) {
      // This is a click on icon or text, not the toggle button
      event.preventDefault();
      event.stopPropagation();

      // Call Show All functions
      switch (eventKey) {
        case "0": // Todo List
          if (checklistRef.current && checklistRef.current.triggerShowAll) {
            checklistRef.current.triggerShowAll();
          }
          break;

        case "1": // List
          if (listRef.current && listRef.current.triggerShowAll) {
            listRef.current.triggerShowAll();
          }
          break;

        case "2": // Notes
          if (notesListRef.current && notesListRef.current.triggerShowAll) {
            notesListRef.current.triggerShowAll();
          }
          break;
          
        case "8": // Grocery List
          if (groceryListRef.current && groceryListRef.current.triggerShowAll) {
            groceryListRef.current.triggerShowAll();
          }
          break;
      }
    }
  };

  useEffect(() => {
    const checkSessionType = async () => {
      const chatSessionId = Cookies.get("chat_session_id");

      // If no chat_session_id cookie exists (main session), keep all accordions closed
      if (!chatSessionId || chatSessionId === "undefined") {
        setDefaultActiveKey(null);
        setLoading(false);
        return;
      }

      // Check session type for side/notification sessions
      const result = await isCurrentSessionSideType();
      if (result) {
        setDefaultActiveKey(result.toString());
      } else {
        // If session exists but is main type, keep all accordions closed
        setDefaultActiveKey(null);
      }
      setLoading(false); // Set loading to false after fetching the result
    };
    checkSessionType();
    if (isActiveSideConvoChannel > 0) {
    }
  }, [isActiveSideConvoChannel]);

  // Auto-expand Voice Assistant accordion when voice becomes active
  useEffect(() => {
    if (isVoiceActive) {
      setDefaultActiveKey("voice");
    }
  }, [isVoiceActive]);
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const { data: userProfileData, error: userProfileError } =
          await supabase.from("profiles").select("*").eq("id", userId).single();

        if (userProfileError) {
          console.error("Error fetching user profile:", userProfileError);
        } else {
          setUserProfile(userProfileData);
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      }
    };

    if (userId) {
      fetchUserProfile();
    }
  }, [userId]);

  useEffect(() => {
    const fetchCustomChatBotList = async () => {
      try {
        const { data: customChatBotData, error: customChatBotError } =
          await supabase
            .from("custom_chat_bot")
            .select("*")
            .eq("user_id", userId)
            .order("updated_at", { ascending: false });

        if (customChatBotError) {
          console.error(
            "Error fetching custom chat bot list:",
            customChatBotError
          );
        } else {
          setCustomChatBotList(customChatBotData);
        }
      } catch (error) {
        console.error("Unexpected error:", error.message);
      }
    };

    if (userId) {
      fetchCustomChatBotList();
    }
  }, [userId, isActiveCustomBotChannel]);

  const handleMainSessionClick = async () => {
    Cookies.remove("chat_session_id"); // Remove the chat_session_id cookie
    // navigate('/chat-bot');
    window.location.reload(); // Navigate to the chat-bot route
  };

  return (
    <div className="sidebar_main_div">
      <div className="sidebar-logo-container p-3">
        <Link className="navbar-brand zoomey-logo-link" to="#" onClick={handleMainSessionClick}>
          <div className="zoomey-logo-wrapper">
            {/* Animated Logo Mark */}
            <div className={`zoomey-logo-mark ${isDarkMode ? 'dark' : 'light'}`}>
              <div className="logo-glow"></div>
              <div className="logo-inner">
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M6 7H22L8 21H22"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="z-path"
                  />
                  <circle cx="22" cy="7" r="2" fill="currentColor" className="z-dot"/>
                </svg>
              </div>
            </div>
            {/* Brand Text */}
            <div className="zoomey-brand-text">
              <span className={`brand-name ${isDarkMode ? 'dark' : 'light'}`}>ZOOMEY</span>
            </div>
          </div>
        </Link>
      </div>
      
      <Accordion
        activeKey={defaultActiveKey}
        onSelect={(key) => {
          if (key === "scheduled-tasks") {
            onOpenScheduledTasks();
            return;
          }
          setDefaultActiveKey(key);
          if (onTabChange) onTabChange(key);
        }}
        flush
        className="sidebar-accordion"
      >
        {/* Voice Assistant - SINGLE instance, always mounted */}
        {/* Accordion item visible only when voice is active, but component always exists */}
        <Accordion.Item
          eventKey="voice"
          className={isVoiceActive ? '' : 'd-none'}
        >
          <Accordion.Header>
            <div className="sidebar_acordian_header">
              <i className="bi bi-mic-fill accordion-menu-icon-font" style={{ color: '#10B981' }}></i>
              <span className="accordion-dropdown-font">Voice Assistant</span>
            </div>
          </Accordion.Header>
          <Accordion.Body>
            {/* Single instance - always mounted, showUI controls visibility */}
            <OpenAIRealtimeTest
              ref={voiceRef}
              showUI={isVoiceActive}
              onVoiceStart={onVoiceStart}
              onVoiceEnd={onVoiceEnd}
              onVoiceTranscriptUpdate={onVoiceTranscriptUpdate}
              onVoiceVideoChange={onVoiceVideoChange}
              showTranscript={false}
            />
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="4">
          <Accordion.Header>
            <div className="sidebar_acordian_header">
              <i className="bi bi-chat-dots accordion-menu-icon-font"></i>
              <span className="accordion-dropdown-font">Topics</span>
            </div>
          </Accordion.Header>
          <Accordion.Body>
            <MainSession
              isDarkMode={isDarkMode}
              isSideConvoChannelUpdateList={isSideConvoChannelUpdate}
              isSidebarVisible={isSidebarVisible}
            />
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="0">
          <Accordion.Header>
            <div
              className="sidebar_acordian_header"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (checklistRef.current && checklistRef.current.triggerShowAll) {
                  checklistRef.current.triggerShowAll();
                }
              }}
            >
              <i className="bi bi-check2-square accordion-menu-icon-font"></i>
              <span className="accordion-dropdown-font">To Do List</span>
            </div>
            <i
              className="bi bi-share sidebar-share-icon"
              title="Share To Do List"
              onClick={(e) => { e.stopPropagation(); setShowShareTaskPopup(true); }}
            ></i>
          </Accordion.Header>
          <Accordion.Body>
            <Checklist
              ref={checklistRef}
              isDarkMode={isDarkMode}
              isCategoryUpdateList={isCategoryUpdate}
              isTaskChannelUpdateList={isTaskChannelUpdate}
              isTaskProfileChannelList={isTaskProfileChannel}
              isSidebarVisible={isSidebarVisible}
            />
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="1">
          <Accordion.Header>
            <div
              className="sidebar_acordian_header"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (listRef.current && listRef.current.triggerShowAll) {
                  listRef.current.triggerShowAll();
                }
              }}
            >
              <i className="bi bi-list-task accordion-menu-icon-font"></i>
              <span className="accordion-dropdown-font">List</span>
            </div>
            <i
              className="bi bi-share sidebar-share-icon"
              title="Share List"
              onClick={(e) => { e.stopPropagation(); setShowShareListPopup(true); }}
            ></i>
          </Accordion.Header>
          <Accordion.Body>
            <List
              ref={listRef}
              isDarkMode={isDarkMode}
              isListCategoryUpdate={isListCategoryUpdate}
              isListDataChannelUpdate={isListDataChannelUpdate}
              isListProfileChannel={isListProfileChannel}
              isSidebarVisible={isSidebarVisible}
            />
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="2">
          <Accordion.Header>
            <div
              className="sidebar_acordian_header"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (notesListRef.current && notesListRef.current.triggerShowAll) {
                  notesListRef.current.triggerShowAll();
                }
              }}
            >
              <i className="bi bi-journal-text accordion-menu-icon-font"></i>
              <span className="accordion-dropdown-font">Notes</span>
            </div>
            <i
              className="bi bi-share sidebar-share-icon"
              title="Share Notes"
              onClick={(e) => { e.stopPropagation(); setShowShareNotesPopup(true); }}
            ></i>
          </Accordion.Header>
          <Accordion.Body>
            <NotesList
              ref={notesListRef}
              isDarkMode={isDarkMode}
              isNotesCategoryUpdate={isNotesCategoryUpdate}
              isNotesDataChannelUpdate={isNotesDataChannelUpdate}
              isnotesProfileChannel={isNotesProfileChannel}
              isSidebarVisible={isSidebarVisible}
            />
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="8">
          <Accordion.Header>
            <div
              className="sidebar_acordian_header"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (groceryListRef.current && groceryListRef.current.triggerShowAll) {
                  groceryListRef.current.triggerShowAll();
                }
              }}
            >
              <i className="bi bi-cart accordion-menu-icon-font"></i>
              <span className="accordion-dropdown-font">Grocery List</span>
            </div>
            <i
              className="bi bi-share sidebar-share-icon"
              title="Share Grocery List"
              onClick={(e) => { e.stopPropagation(); setShowShareGroceryPopup(true); }}
            ></i>
          </Accordion.Header>
          <Accordion.Body>
            <GroceryList ref={groceryListRef} isDarkMode={isDarkMode} />
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="9">
          <Accordion.Header>
            <div
              className="sidebar_acordian_header"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (ideasListRef.current && ideasListRef.current.triggerShowAll) {
                  ideasListRef.current.triggerShowAll();
                }
              }}
            >
              <i className="bi bi-lightbulb accordion-menu-icon-font"></i>
              <span className="accordion-dropdown-font">Ideas</span>
            </div>
          </Accordion.Header>
          <Accordion.Body>
            <IdeasList ref={ideasListRef} userId={userId} />
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="5">
          <Accordion.Header>
            <div className="sidebar_acordian_header">
              <i className="bi bi-bell accordion-menu-icon-font"></i>
              <span className="accordion-dropdown-font">Notification Chats</span>
            </div>
          </Accordion.Header>
          <Accordion.Body>
            <NotificationSession
              isDarkMode={isDarkMode}
              isNotificationSessionChannel={isNotificationSessionChannel}
              isSidebarVisible={isSidebarVisible}
            />
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="sharing">
          <Accordion.Header>
            <div className="sidebar_acordian_header" style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center" }}>
                <i className="bi bi-share accordion-menu-icon-font"></i>
                <span className="accordion-dropdown-font">Sharing</span>
              </div>
              {sharingPendingCount > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: "4px", marginRight: "8px" }}>
                  {sharingBreakdown.tasks > 0 && (
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: "3px",
                      background: "#ef4444", color: "#fff",
                      fontSize: "0.55rem", fontWeight: 700,
                      height: "20px", padding: "0 6px",
                      borderRadius: "4px", lineHeight: 1,
                    }}>
                      <i className="bi bi-check2-square" style={{ fontSize: "0.6rem" }}></i>
                      {sharingBreakdown.tasks > 99 ? "99+" : sharingBreakdown.tasks}
                    </span>
                  )}
                  {sharingBreakdown.lists > 0 && (
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: "3px",
                      background: "#ef4444", color: "#fff",
                      fontSize: "0.55rem", fontWeight: 700,
                      height: "20px", padding: "0 6px",
                      borderRadius: "4px", lineHeight: 1,
                    }}>
                      <i className="bi bi-list-task" style={{ fontSize: "0.6rem" }}></i>
                      {sharingBreakdown.lists > 99 ? "99+" : sharingBreakdown.lists}
                    </span>
                  )}
                  {sharingBreakdown.notes > 0 && (
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: "3px",
                      background: "#ef4444", color: "#fff",
                      fontSize: "0.55rem", fontWeight: 700,
                      height: "20px", padding: "0 6px",
                      borderRadius: "4px", lineHeight: 1,
                    }}>
                      <i className="bi bi-journal-text" style={{ fontSize: "0.6rem" }}></i>
                      {sharingBreakdown.notes > 99 ? "99+" : sharingBreakdown.notes}
                    </span>
                  )}
                  {sharingBreakdown.grocery > 0 && (
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: "3px",
                      background: "#ef4444", color: "#fff",
                      fontSize: "0.55rem", fontWeight: 700,
                      height: "20px", padding: "0 6px",
                      borderRadius: "4px", lineHeight: 1,
                    }}>
                      <i className="bi bi-cart" style={{ fontSize: "0.6rem" }}></i>
                      {sharingBreakdown.grocery > 99 ? "99+" : sharingBreakdown.grocery}
                    </span>
                  )}
                  {sharingBreakdown.pending > 0 && (
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: "3px",
                      background: "#f59e0b", color: "#fff",
                      fontSize: "0.55rem", fontWeight: 700,
                      height: "20px", padding: "0 6px",
                      borderRadius: "4px", lineHeight: 1,
                    }}>
                      <i className="bi bi-person-plus" style={{ fontSize: "0.6rem" }}></i>
                      {sharingBreakdown.pending}
                    </span>
                  )}
                </div>
              )}
            </div>
          </Accordion.Header>
          <Accordion.Body>
            <SharedWithMe
              userId={userId}
              isDarkMode={isDarkMode}
              onPendingCountChange={(count, breakdown) => { setSharingPendingCount(count); if (breakdown) setSharingBreakdown(breakdown); }}
            />
          </Accordion.Body>
        </Accordion.Item>

        {/* Scheduled Tasks - opens modal */}
        <Accordion.Item eventKey="scheduled-tasks" className="no-expand">
          <Accordion.Header onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onOpenScheduledTasks();
          }}>
            <div className="sidebar_acordian_header" onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onOpenScheduledTasks();
            }}>
              <i className="bi bi-clock-history accordion-menu-icon-font"></i>
              <span className="accordion-dropdown-font">Scheduled Tasks</span>
            </div>
          </Accordion.Header>
        </Accordion.Item>

        <Accordion.Item eventKey="3">
          <Accordion.Header>
            <div className="sidebar_acordian_header">
              <i className="bi bi-box-seam accordion-menu-icon-font"></i>
              <span className="accordion-dropdown-font">Packs</span>
            </div>
            <span className="coming-soon-badge">Coming Soon</span>
          </Accordion.Header>
          <Accordion.Body>
            <ul className="px-3 ps-4">
              <li>
                <Link to="#">
                  <i className="bi bi-house-heart"></i>
                  Family
                </Link>
              </li>
              <li>
                <Link to="#">
                  <i className="bi bi-mortarboard"></i>
                  School
                </Link>
              </li>
              <li>
                <Link to="#">
                  <i className="bi bi-bullseye"></i>
                  Focus Coach
                </Link>
              </li>
            </ul>
          </Accordion.Body>
        </Accordion.Item>

        {/* <Accordion.Item eventKey="6">
          <Accordion.Header>
            <div className="sidebar_acordian_header">
              <i className="bi bi-people-fill accordion-menu-icon-font"></i>
              <span className="accordion-dropdown-font">Messenger</span>
              <span className="coming-soon-badge">Coming Soon</span>
            </div>
          </Accordion.Header>
          <Accordion.Body>
            <div className="coming-soon-message">
              <i className="bi bi-rocket-takeoff"></i>
              <p>Coming soon! Stay tuned for updates.</p>
            </div>
          </Accordion.Body>
        </Accordion.Item> */}
      </Accordion>

      <SidebarCredits userId={userId} isDarkMode={isDarkMode} />

      {userId && (
        <ShareSectionPopup
          isDarkMode={isDarkMode}
          show={showShareTaskPopup}
          handleClose={() => setShowShareTaskPopup(false)}
          UserId={userId}
          sectionType="tasks"
        />
      )}
      {userId && (
        <ShareSectionPopup
          isDarkMode={isDarkMode}
          show={showShareListPopup}
          handleClose={() => setShowShareListPopup(false)}
          UserId={userId}
          sectionType="lists"
        />
      )}
      {userId && (
        <ShareSectionPopup
          isDarkMode={isDarkMode}
          show={showShareNotesPopup}
          handleClose={() => setShowShareNotesPopup(false)}
          UserId={userId}
          sectionType="notes"
        />
      )}
      {userId && (
        <ShareSectionPopup
          isDarkMode={isDarkMode}
          show={showShareGroceryPopup}
          handleClose={() => setShowShareGroceryPopup(false)}
          UserId={userId}
          sectionType="grocery"
        />
      )}
    </div>
  );
}

export default Sidebar;
